import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import productsData from "../data/products.json";

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);

  // Get all products from all category arrays
  const allProducts = [
    ...productsData.mobiles,
    ...productsData.leds,
    ...productsData.homeAppliances,
    ...productsData.watches,
    ...productsData.audio,
    ...productsData.tablets,
    ...productsData.earbuds,
    ...productsData.soundbars,
    ...productsData.projectors
  ];

  // Find the product by ID
  const product = allProducts.find(p => p.id === parseInt(id));

  // Scroll to top when product page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Set default color when product loads
  useEffect(() => {
    if (product?.colors && product.colors.length > 0) {
      setSelectedColor(product.colors[0]);
      setSelectedColorIndex(0);
    }
  }, [product]);

  // Set default size for LEDs and Home Appliances when product loads
  useEffect(() => {
    if (product?.availableSizes && product.availableSizes.length > 0) {
      setSelectedSize(product.availableSizes[0]);
    }
  }, [product]);

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="container">
          <div className="product-not-found">
            <h2>Product Not Found</h2>
            <p>The product you're looking for doesn't exist.</p>
            <button className="btn-primary" onClick={() => navigate("/products")}>
              Back to Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleImageError = () => {
    setImageError(true);
  };

  const handleWhatsAppOrder = () => {
    const specs = [];
    if (product.ram) specs.push(`RAM: ${product.ram}`);
    if (product.storage) specs.push(`Storage: ${product.storage}`);
    if (product.size) specs.push(`Size: ${product.size}`);
    if (product.channels) specs.push(product.channels);
    if (product.type) specs.push(product.type);

    const specsText = specs.length > 0 ? `\nSpecs: ${specs.join(', ')}` : '';
    const colorText = selectedColor ? `\n🎨 Color: *${selectedColor}*` : '';
    const discountText = product.discount > 0 ? `\nOriginal Price: PKR ${product.originalPrice.toLocaleString()}\nDiscount: ${product.discount}%` : '';

    const message = `Hi *Samsung Door Step*! 👋\n\nI'm interested in:\n\n📱 *${product.name}*${specsText}${colorText}\n💰 Price: *PKR ${product.price.toLocaleString()}*${discountText}\n\nPlease provide more details.`;
    const whatsappURL = `https://wa.me/923292737737?text=${encodeURIComponent(message)}`;
    window.open(whatsappURL, "_blank");
  };

  // Get current price based on selected size (for LEDs and Home Appliances) or default price
  const getCurrentPrice = () => {
    if (selectedSize) {
      return {
        price: selectedSize.price,
        originalPrice: selectedSize.originalPrice,
        discount: selectedSize.discount
      };
    }
    return {
      price: product.price,
      originalPrice: product.originalPrice,
      discount: product.discount
    };
  };

  const currentPriceInfo = getCurrentPrice();
  const discountAmount = currentPriceInfo.originalPrice ? currentPriceInfo.originalPrice - currentPriceInfo.price : 0;

  // Get current display image based on selected color
  const getCurrentImage = () => {
    // If product has colorImages and a color is selected
    if (product?.colorImages && product.colorImages.length > 0) {
      // If we have enough images, use the exact index
      if (selectedColorIndex < product.colorImages.length) {
        return product.colorImages[selectedColorIndex];
      }
      // If not enough images, cycle through available ones (modulo)
      const imageIndex = selectedColorIndex % product.colorImages.length;
      return product.colorImages[imageIndex];
    }
    // Otherwise show the default product image
    return product.image;
  };

  // Helper function to map color names to hex codes
  const getColorHex = (colorName) => {
    const colorMap = {
      // Blacks & Grays
      'Black': '#000000',
      'Titanium Black': '#2C2C2E',
      'Graphite': '#3A3A3C',
      'Awesome Graphite': '#3A3A3C',
      'Dark Gray': '#48484A',
      'Titanium Gray': '#8E8E93',
      'Gray': '#8E8E93',
      'Awesome Lightgray': '#C7C7CC',

      // Whites & Silvers
      'White': '#FFFFFF',
      'Titanium White Silver': '#E5E5EA',
      'Silver': '#C0C0C0',
      'Beige': '#F5F5DC',
      'Modern White': '#FAFAFA',

      // Blues
      'Light Blue': '#5AC8FA',
      'Titanium Silver Blue': '#6BA4D8',
      'Awesome Iceblue': '#00A9E0',
      'Iceblue': '#00A9E0',
      'Navy': '#003366',
      'Awesome Navy': '#003366',
      'Blue Black': '#1A237E',
      'Brushed Navy': '#2C3E50',

      // Purples & Pinks
      'Violet': '#8B7BA8',
      'Awesome Violet': '#8B7BA8',
      'Lilac': '#C8A2C8',
      'Awesome Lilac': '#C8A2C8',
      'Light Violet': '#DDA0DD',
      'Bora Purple': '#9370DB',
      'Pink Gold': '#E6A8D7',
      'Titanium Pink Gold': '#E6A8D7',
      'Awesome Pink': '#FFB6C1',
      'Peach Pink': '#FFB3A7',

      // Greens & Yellows
      'Green': '#34C759',
      'Light Green': '#90EE90',
      'Titanium Jade Green': '#00A86B',
      'Lime': '#B2FF59',
      'Awesome Lime': '#B2FF59',
      'Mint': '#98FF98',
      'Olive': '#808000',
      'Awesome Olive': '#808000',
      'Yellow': '#FFD60A',
      'Awesome Lemon': '#FFF44F',
      'Lemon': '#FFF44F',

      // Golds & Browns
      'Gold': '#FFD700',
      'Sand Gold Metal': '#D4AF37',
      'Modern Teak': '#B5651D',
      'Modern Brown': '#8B4513',

      // Special Colors
      'Titanium': '#878681',
      'Titanium Jet Black': '#0A0A0A',
      'Black Caviar': '#1C1C1C',
      'Deep Charcoal': '#36454F',
      'Lavender Gray': '#C4C3D0',
      'Titan Gray': '#808080',
      'Titan Black': '#1A1A1A',
    };

    return colorMap[colorName] || '#CCCCCC'; // Default gray if not found
  };

  return (
    <div className="product-detail-page">
      <div className="container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <span onClick={() => navigate("/")} className="breadcrumb-link">Home</span>
          <span className="breadcrumb-separator">/</span>
          <span onClick={() => navigate("/products")} className="breadcrumb-link">Products</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{product.category}</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{product.name}</span>
        </div>

        <div className="product-detail-container">
          {/* Product Image Section */}
          <div className="product-detail-image-wrapper">
            <div className="product-detail-image">
              {imageError ? (
                <div className="image-placeholder-detail">
                  <p>{product.name}</p>
                  <span>Image not available</span>
                </div>
              ) : (
                <img
                  src={getCurrentImage()}
                  alt={`${product.name} - ${selectedColor || ''}`}
                  onError={handleImageError}
                  key={selectedColorIndex}
                />
              )}
            </div>

            {/* Thumbnail Gallery for Color Images */}
            {product.colorImages && product.colorImages.length > 1 && (
              <div className="image-thumbnails">
                {product.colorImages.map((imgSrc, index) => (
                  <div
                    key={index}
                    className={`thumbnail ${selectedColorIndex === index ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedColorIndex(index);
                      if (product.colors && product.colors[index]) {
                        setSelectedColor(product.colors[index]);
                      }
                      setImageError(false);
                    }}
                  >
                    <img src={imgSrc} alt={`${product.name} - View ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Info Section */}
          <div className="product-detail-info">
            <div className="product-category-badge">{product.category}</div>
            <h1 className="product-detail-title">{product.name}</h1>

            {/* Brand */}
            {product.brand && (
              <div className="product-brand">
                <strong>Brand:</strong> {product.brand}
              </div>
            )}

            {/* Price Section */}
            <div className="product-detail-price">
              <div className="current-price">
                PKR {currentPriceInfo.price.toLocaleString()}
              </div>
              {discountAmount > 0 && (
                <div className="price-discount-section">
                  <span className="original-price">PKR {currentPriceInfo.originalPrice.toLocaleString()}</span>
                  <span className="discount-badge">Save PKR {discountAmount.toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Capacity Selector for Home Appliances (Washing Machines) */}
            {product.category === 'Home Appliances' && product.availableSizes && product.availableSizes.length > 0 && selectedSize && (
              <div className="capacity-detail-selector">
                <h3>Choose Capacity:</h3>
                <div className="capacity-options-detail">
                  {product.availableSizes.map((sizeOption, index) => (
                    <button
                      key={index}
                      className={`capacity-option-btn ${selectedSize.capacity === sizeOption.capacity ? 'active' : ''}`}
                      onClick={() => setSelectedSize(sizeOption)}
                    >
                      <span className="capacity-value">{sizeOption.capacity}</span>
                      <span className="capacity-price">PKR {sizeOption.price.toLocaleString()}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Size Selector for LEDs */}
            {product.category === 'LEDs' && product.availableSizes && product.availableSizes.length > 0 && selectedSize && (
              <div className="led-detail-size-selector">
                <h3>Choose Screen Size:</h3>

                {/* Size Options Grid */}
                <div className="size-options-grid">
                  {product.availableSizes.map((sizeOption, index) => (
                    <button
                      key={index}
                      className={`size-option-card ${selectedSize.size === sizeOption.size ? 'active' : ''}`}
                      onClick={() => setSelectedSize(sizeOption)}
                    >
                      <div className="size-option-header">
                        <span className="size-icon">📺</span>
                        <span className="size-value">{sizeOption.size}</span>
                      </div>

                      {sizeOption.width && sizeOption.height && (
                        <div className="size-option-dimensions">
                          <span className="dimension-icon">📏</span>
                          <span className="dimension-value">{sizeOption.width}" × {sizeOption.height}"</span>
                        </div>
                      )}

                      <div className="size-option-price">
                        <span className="price-label">PKR</span>
                        <span className="price-value">{sizeOption.price.toLocaleString()}</span>
                      </div>

                      {sizeOption.discount > 0 && (
                        <div className="size-option-savings">
                          Save PKR {(sizeOption.originalPrice - sizeOption.price).toLocaleString()}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selector - Hide for LEDs */}
            {product.colors && product.colors.length > 0 && product.category !== 'LEDs' && (
              <div className="color-selector">
                <h3>Choose Color:</h3>
                <p className="selected-color-name">{selectedColor}</p>
                <div className="color-options">
                  {product.colors.map((color, index) => (
                    <button
                      key={index}
                      className={`color-option ${selectedColor === color ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedColor(color);
                        setSelectedColorIndex(index);
                        setImageError(false);
                      }}
                      title={color}
                    >
                      <span
                        className="color-box"
                        style={{
                          backgroundColor: getColorHex(color),
                          border: getColorHex(color) === '#FFFFFF' || getColorHex(color) === '#FAFAFA' ? '1px solid #ddd' : 'none'
                        }}
                      />
                      <span className="color-name">{color}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Specs */}
            <div className="quick-specs">
              {product.ram && (
                <div className="spec-item">
                  <span className="spec-label">RAM</span>
                  <span className="spec-value">{product.ram}</span>
                </div>
              )}
              {product.storage && (
                <div className="spec-item">
                  <span className="spec-label">Storage</span>
                  <span className="spec-value">{product.storage}</span>
                </div>
              )}
              {/* Hide size for LEDs since it's shown above with dimensions */}
              {product.size && product.category !== 'LEDs' && (
                <div className="spec-item">
                  <span className="spec-label">Size</span>
                  <span className="spec-value">{product.size}</span>
                </div>
              )}
              {product.capacity && (
                <div className="spec-item">
                  <span className="spec-label">Capacity</span>
                  <span className="spec-value">{product.capacity}</span>
                </div>
              )}
              {product.type && (
                <div className="spec-item">
                  <span className="spec-label">Type</span>
                  <span className="spec-value">{product.type}</span>
                </div>
              )}
              {product.channels && (
                <div className="spec-item">
                  <span className="spec-label">Channels</span>
                  <span className="spec-value">{product.channels}</span>
                </div>
              )}
              {product.series && (
                <div className="spec-item">
                  <span className="spec-label">Series</span>
                  <span className="spec-value">{product.series}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="product-actions">
              <button className="btn-order-now" onClick={handleWhatsAppOrder}>
                Order via WhatsApp
              </button>
              <button className="btn-call-now" onClick={() => window.location.href = 'tel:+923292737737'}>
                Call Now
              </button>
            </div>

            {/* Delivery Info */}
            <div className="delivery-info">
              <div className="delivery-item">
                <span className="delivery-icon">🚚</span>
                <span>Free Doorstep Delivery</span>
              </div>
              <div className="delivery-item">
                <span className="delivery-icon">✅</span>
                <span>Cash on Delivery Available</span>
              </div>
              <div className="delivery-item">
                <span className="delivery-icon">🔒</span>
                <span>Warranty Included</span>
              </div>
            </div>
          </div>
        </div>

        {/* Specifications Section */}
        {product.specs && product.specs.length > 0 && (
          <div className="product-specifications">
            <h2>Specifications</h2>
            <div className="specs-grid">
              {product.specs.map((spec, index) => (
                <div key={index} className="spec-card">
                  <span className="spec-icon">✓</span>
                  <span className="spec-text">{spec}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Details Section */}
        <div className="additional-details">
          <h2>Product Details</h2>
          <div className="details-grid">
            {product.resolution && (
              <div className="detail-item">
                <strong>Resolution:</strong>
                <span>{product.resolution}</span>
              </div>
            )}
            {product.refreshRate && (
              <div className="detail-item">
                <strong>Refresh Rate:</strong>
                <span>{product.refreshRate}</span>
              </div>
            )}
            {product.brightness && (
              <div className="detail-item">
                <strong>Brightness:</strong>
                <span>{product.brightness}</span>
              </div>
            )}
            {product.smartTV !== undefined && (
              <div className="detail-item">
                <strong>Smart TV:</strong>
                <span>{product.smartTV ? "Yes" : "No"}</span>
              </div>
            )}
            {product.hdmi && (
              <div className="detail-item">
                <strong>HDMI Ports:</strong>
                <span>{product.hdmi}</span>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        <div className="related-products-section">
          <h2>Similar Products</h2>
          <div className="related-products-grid">
            {allProducts
              .filter(p => p.category === product.category && p.id !== product.id)
              .slice(0, 4)
              .map(relatedProduct => (
                <div
                  key={relatedProduct.id}
                  className="related-product-card"
                  onClick={() => navigate(`/product/${relatedProduct.id}`)}
                >
                  <div className="related-product-image">
                    <img src={relatedProduct.image} alt={relatedProduct.name} />
                  </div>
                  <h4>{relatedProduct.name}</h4>
                  <p className="related-price">PKR {relatedProduct.price.toLocaleString()}</p>
                  {relatedProduct.originalPrice && relatedProduct.originalPrice > relatedProduct.price && (
                    <span className="related-discount">Save PKR {(relatedProduct.originalPrice - relatedProduct.price).toLocaleString()}</span>
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetail;
